// src/config/threats.ts — the threat catalogue of SPEC §3.5.1, as data.
// Distances metres, times seconds, angles degrees at this boundary only.

import { MISSILE_MANPADS, MISSILE_SHORAD, SHORAD } from './tuning';
import type { MissileParams } from './weapons';

export type { MissileParams };

/**
 * Threats are layered so that no single evasion technique solves the game (SPEC §3.5).
 *
 * Each entry is a complete `ThreatParams` per SPEC §6.3. The numbers come straight from the
 * catalogue table; where the table gives a range ("2.0–4.0 s") the value chosen sits where that
 * threat's role in the campaign puts it — a MANPADS team reacts faster than a battery crew, an
 * area-defence battery is slower and further away than anything else.
 */

export const ThreatClass = {
  ShoradTracked: 'SHORAD_TRACKED',
  ShoradGun: 'SHORAD_GUN',
  Manpads: 'MANPADS',
  NavalCiws: 'NAVAL_CIWS',
  MediumSam: 'MEDIUM_SAM',
  EwJammer: 'EW_JAMMER',
} as const;
export type ThreatClassId = (typeof ThreatClass)[keyof typeof ThreatClass];

export interface ThreatParams {
  readonly id: ThreatClassId;
  /** Short label on the RWR ring — two to four characters (SPEC §3.4.1). */
  readonly rwrLabel: string;
  readonly displayName: string;

  /**
   * False for a passive threat. A MANPADS team gives **no radar warning at all**: it never
   * appears on the RWR, which is exactly why it is dangerous and why the campaign teaches it
   * with a visual cue rather than an instrument one.
   */
  readonly emitsRadar: boolean;

  searchRangeM: number;
  trackRangeM: number;
  minEngageM: number;
  maxEngageM: number;
  minAltM: number;
  maxAltM: number;

  acquireTimeS: number;
  reactionTimeS: number;
  trackMemoryS: number;

  /** Doppler notch half-width, m/s. Zero for a threat with no Doppler processing. */
  notchVelocityMs: number;

  salvoSize: number;
  salvoIntervalS: number;
  reloadS: number;
  magazine: number;

  missile: MissileParams;
  radarMastHeightM: number;
  /** 0–1, scales reaction and re-acquire time. */
  crewQuality: number;
}

/**
 * The reference threat, and the one the whole evasion model is taught against: tracked SHORAD
 * with radar command guidance. Its numbers live in `tuning.ts` because M1–M4 already referenced
 * them; everything else in this file is defined here.
 */
const SHORAD_TRACKED: ThreatParams = {
  id: ThreatClass.ShoradTracked,
  rwrLabel: 'SA',
  displayName: 'TRACKED SHORAD',
  emitsRadar: true,
  searchRangeM: SHORAD.searchRangeM,
  trackRangeM: SHORAD.trackRangeM,
  minEngageM: SHORAD.minEngageM,
  maxEngageM: SHORAD.maxEngageM,
  minAltM: SHORAD.minAltM,
  maxAltM: SHORAD.maxAltM,
  acquireTimeS: SHORAD.acquireTimeS,
  reactionTimeS: SHORAD.reactionTimeS,
  trackMemoryS: SHORAD.trackMemoryS,
  notchVelocityMs: 22,
  salvoSize: SHORAD.salvoSize,
  salvoIntervalS: SHORAD.salvoIntervalS,
  reloadS: SHORAD.reloadS,
  magazine: SHORAD.magazine,
  missile: MISSILE_SHORAD,
  radarMastHeightM: SHORAD.radarMastHeightM,
  crewQuality: SHORAD.crewQuality,
};

/**
 * Radar-directed twin 30 mm. Short-legged and lethal inside 3 km; the counter is to stay outside
 * it, so its search range is deliberately tight enough that the player can learn where the edge is.
 */
const SHORAD_GUN: ThreatParams = {
  id: ThreatClass.ShoradGun,
  rwrLabel: 'AAA',
  displayName: 'RADAR AAA',
  emitsRadar: true,
  searchRangeM: 6000,
  trackRangeM: 4500,
  minEngageM: 300,
  maxEngageM: 3000,
  minAltM: 0,
  maxAltM: 3000,
  acquireTimeS: 1.6,
  reactionTimeS: 1.2,
  trackMemoryS: 2.5,
  notchVelocityMs: 18,
  salvoSize: 1,
  salvoIntervalS: 0.4,
  reloadS: 6,
  magazine: 40,
  missile: MISSILE_SHORAD,
  radarMastHeightM: 3.5,
  crewQuality: 0.75,
};

/**
 * Shoulder-launched IR. No radar, so no warning — and rear-aspect biased, so the counter is to
 * deny it the tailpipe rather than to out-turn it.
 */
const MANPADS: ThreatParams = {
  id: ThreatClass.Manpads,
  rwrLabel: 'MP',
  displayName: 'MANPADS TEAM',
  emitsRadar: false,
  searchRangeM: 5000,
  trackRangeM: 5000,
  minEngageM: 500,
  maxEngageM: 5000,
  minAltM: 0,
  maxAltM: 3000,
  acquireTimeS: 3.0,
  reactionTimeS: 2.2,
  trackMemoryS: 2.0,
  // Passive IR has no Doppler processing: notching a MANPADS does nothing, which is the lesson.
  notchVelocityMs: 0,
  salvoSize: 1,
  salvoIntervalS: 3.0,
  reloadS: 20,
  magazine: 2,
  missile: MISSILE_MANPADS,
  radarMastHeightM: 1.8,
  crewQuality: 0.6,
};

/** Ship point defence. Sits on water, so it is never terrain-masked — only range and notch work. */
const NAVAL_CIWS: ThreatParams = {
  id: ThreatClass.NavalCiws,
  rwrLabel: 'CIWS',
  displayName: 'NAVAL CIWS',
  emitsRadar: true,
  searchRangeM: 12000,
  trackRangeM: 9000,
  minEngageM: 500,
  maxEngageM: 6000,
  minAltM: 0,
  maxAltM: 4000,
  acquireTimeS: 2.0,
  reactionTimeS: 1.5,
  trackMemoryS: 3.5,
  notchVelocityMs: 20,
  salvoSize: 2,
  salvoIntervalS: 1.0,
  reloadS: 10,
  magazine: 16,
  missile: MISSILE_SHORAD,
  radarMastHeightM: 18,
  crewQuality: 0.8,
};

/**
 * Area defence. The mast is high and the range is enormous, so the only counters are the radar
 * horizon and terrain — fly below it or put a ridge in the way. Nothing else reaches.
 */
const MEDIUM_SAM: ThreatParams = {
  id: ThreatClass.MediumSam,
  rwrLabel: 'SAM',
  displayName: 'AREA SAM',
  emitsRadar: true,
  searchRangeM: 60000,
  trackRangeM: 40000,
  minEngageM: 3000,
  maxEngageM: 35000,
  minAltM: 50,
  maxAltM: 15000,
  acquireTimeS: 4.0,
  reactionTimeS: 3.0,
  trackMemoryS: 6.0,
  notchVelocityMs: 25,
  salvoSize: 2,
  salvoIntervalS: 2.5,
  reloadS: 25,
  magazine: 8,
  missile: MISSILE_SHORAD,
  radarMastHeightM: 22,
  crewQuality: 0.85,
};

export const THREATS: Record<ThreatClassId, ThreatParams> = {
  [ThreatClass.ShoradTracked]: SHORAD_TRACKED,
  [ThreatClass.ShoradGun]: SHORAD_GUN,
  [ThreatClass.Manpads]: MANPADS,
  [ThreatClass.NavalCiws]: NAVAL_CIWS,
  [ThreatClass.MediumSam]: MEDIUM_SAM,
  // Area denial only: no state machine, no launcher. Handled entirely by threat/EW.ts.
  [ThreatClass.EwJammer]: {
    id: ThreatClass.EwJammer,
    rwrLabel: 'EW',
    displayName: 'GPS/DATALINK JAMMER',
    emitsRadar: false,
    searchRangeM: 0,
    trackRangeM: 0,
    minEngageM: 0,
    maxEngageM: 0,
    minAltM: 0,
    maxAltM: 0,
    acquireTimeS: 0,
    reactionTimeS: 0,
    trackMemoryS: 0,
    notchVelocityMs: 0,
    salvoSize: 0,
    salvoIntervalS: 0,
    reloadS: 0,
    magazine: 0,
    missile: MISSILE_SHORAD,
    radarMastHeightM: 8,
    crewQuality: 0,
  },
};

/** One placed threat. Missions supply these at M8; see DEMO_THREAT_LAYOUT until then. */
export interface ThreatPlacement {
  readonly designation: string;
  readonly type: ThreatClassId;
  /** Ground position, metres. The site sits on the terrain; y comes from the heightfield. */
  readonly xM: number;
  readonly zM: number;
}

/**
 * Placeholder *data*, not a placeholder implementation — the same arrangement the bearing tags
 * use until M8. Sites are placed on real features of the Kerani theatre so that the acceptance
 * criteria can be flown: the port battery covers the bay, the ridge site forces the valley
 * approach, and the area SAM inland punishes anyone who climbs.
 */
export const DEMO_THREAT_LAYOUT: readonly ThreatPlacement[] = [
  // Guarding the port on the bay's north shore — the first thing an easterly ingress meets.
  { designation: 'K-2', type: ThreatClass.ShoradTracked, xM: 4250, zM: 800 },
  // On the sand spit across the bay mouth: no terrain to hide behind, so it teaches the notch.
  { designation: 'K-4', type: ThreatClass.ShoradTracked, xM: 4600, zM: 2400 },
  // Gun position on the beach, close in — punishes a low straight run down the coast.
  { designation: 'G-1', type: ThreatClass.ShoradGun, xM: 2600, zM: -1200 },
  // MANPADS in the river valley: silent on the RWR, which is the whole point.
  { designation: 'M-3', type: ThreatClass.Manpads, xM: 6400, zM: 1500 },
  // Area SAM behind the spine. Only the radar horizon and the ridge line defeat it.
  { designation: 'S-1', type: ThreatClass.MediumSam, xM: 10500, zM: -600 },
  // Jammer inland of the bay, covering the whole approach corridor.
  { designation: 'E-1', type: ThreatClass.EwJammer, xM: 7200, zM: 2600 },
];
